import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type {
  CatalogueTemplateStatus,
  CatalogueTemplateBase,
} from "@/services/catalogue-template.service";

const statusStyles: Record<CatalogueTemplateStatus, string> = {
  draft: "border-blue-200 bg-blue-50 text-blue-700",
  published: "border-green-200 bg-green-50 text-green-700",
  archived: "border-slate-200 bg-slate-50 text-slate-600",
};

interface TemplateSelectorProps {
  templates: CatalogueTemplateBase[];
  selectedId: string | null;
  loading?: boolean;
  onSelect: (templateId: string) => void;
}

export function TemplateSelector({
  templates,
  selectedId,
  loading = false,
  onSelect,
}: TemplateSelectorProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = templates.find((template) => template.id === selectedId) ?? null;
  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return templates;
    return templates.filter((template) =>
      [template.name, template.version, template.status]
        .join(" ")
        .toLowerCase()
        .includes(normalized)
    );
  }, [query, templates]);

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-auto min-h-10 w-full justify-between px-3 py-2 text-left sm:w-[360px]"
          disabled={loading || templates.length === 0}
          aria-label="Select configuration and version"
        >
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium">
              {loading
                ? "Loading configurations…"
                : selected
                  ? selected.name
                  : "Select a configuration"}
            </span>
            {selected && (
              <span className="block truncate text-xs font-normal text-muted-foreground">
                Version {selected.version} · {selected.status}
                {selected.isDefault ? " · default" : ""}
              </span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(420px,calc(100vw-2rem))] p-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="pl-9"
            placeholder="Search name, version, or status…"
            autoFocus
          />
        </div>
        <div className="mt-2 max-h-72 space-y-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              No configurations match your search.
            </p>
          ) : (
            filtered.map((template) => {
              const isSelected = template.id === selectedId;
              return (
                <button
                  key={template.id}
                  type="button"
                  className={cn(
                    "flex w-full items-start gap-2 rounded-md px-3 py-2 text-left hover:bg-muted",
                    isSelected && "bg-muted"
                  )}
                  onClick={() => {
                    onSelect(template.id);
                    setOpen(false);
                    setQuery("");
                  }}
                >
                  <Check
                    className={cn(
                      "mt-0.5 h-4 w-4 shrink-0",
                      isSelected ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{template.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      Version {template.version}
                      {template.isDefault ? " · default" : ""}
                    </span>
                  </span>
                  <Badge
                    variant="outline"
                    className={cn("shrink-0 capitalize", statusStyles[template.status])}
                  >
                    {template.status}
                  </Badge>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
